import { captureMessage } from '@sentry/react'
import type { z } from 'zod'
import type { SseEventName } from '@/lib/sse-stream-registry'

export interface SseValidationFailure {
    event: SseEventName
    code: 'invalid_json' | 'invalid_payload'
    path: string
}
export type SseValidationErrorHandler = (failure: SseValidationFailure) => void

/** Decode only at the wire boundary; callbacks run after parsing succeeds. */
export function createSseListener<TSchema extends z.ZodType>(
    eventName: SseEventName,
    schema: TSchema,
    handler: (data: z.output<TSchema>) => void,
    onValidationError?: SseValidationErrorHandler,
) {
    // One diagnostic per invalid episode, as with the transport health reports.
    let reported = false
    function fail(code: SseValidationFailure['code'], path = '') {
        const failure = { event: eventName, code, path }
        if (!reported) {
            reported = true
            captureMessage('sse invalid payload', {
                level: 'warning',
                tags: { sse_event: eventName, sse_reason: code },
                extra: { path },
            })
        }
        onValidationError?.(failure)
    }
    return (event: MessageEvent<unknown>) => {
        if (typeof event.data !== 'string') {
            fail('invalid_payload')
            return
        }
        let input: unknown
        try {
            input =
                eventName === 'customer_display_clear' && event.data === ''
                    ? undefined
                    : JSON.parse(event.data)
        } catch {
            fail('invalid_json')
            return
        }
        const result = schema.safeParse(input)
        if (!result.success) {
            fail(
                'invalid_payload',
                result.error.issues[0]?.path.join('.') ?? '',
            )
            return
        }
        reported = false
        handler(result.data)
    }
}
